import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaAward,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaIdBadge,
} from "react-icons/fa";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import PageLoader from "../../components/PageLoader";
import { supabase } from "../../supabaseClient";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import {
  competencyStatusClass,
  formatCompetencyDate,
  getCompetencyStatus,
  isCompetencyRequiredForRole,
} from "../../utils/competencies";

export default function Competencies({ session, profile: suppliedProfile }) {
  const { isEnabled } = useFeatureFlags();
  const [profile, setProfile] = useState(suppliedProfile || null);
  const [catalogue, setCatalogue] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [verificationFeedback, setVerificationFeedback] = useState(null);
  const [verifyingRecordId, setVerifyingRecordId] = useState(null);

  const load = useCallback(async () => {
    if (!session?.user?.id) return;
    setLoading(true);
    setFeedback("");

    let currentProfile = suppliedProfile || profile;
    if (!currentProfile?.job_role) {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, position, job_role")
        .eq("id", session.user.id)
        .maybeSingle();
      if (!error && data) {
        currentProfile = data;
        setProfile(data);
      }
    }

    const [catalogueResult, recordsResult] = await Promise.all([
      supabase
        .from("staff_competency_catalog")
        .select("id, title, category, description, default_validity_months, required_job_roles, renewal_elearning_module_id, renewal_lead_days, active")
        .eq("active", true)
        .order("category", { ascending: true })
        .order("title", { ascending: true }),
      supabase
        .from("staff_competency_records")
        .select("id, competency_id, user_id, achieved_on, expires_on, certificate_reference, evidence_url, evidence_notes, verified, verified_by, verified_at, verification_method, verification_notes, notes, updated_at")
        .eq("user_id", session.user.id)
        .order("expires_on", { ascending: true, nullsFirst: false }),
    ]);

    if (catalogueResult.error || recordsResult.error) {
      console.error("Unable to load staff competencies:", catalogueResult.error || recordsResult.error);
      setCatalogue([]);
      setRecords([]);
      setFeedback("Competencies could not be loaded. Management may need to run ADD_STAFF_COMPETENCIES.sql.");
    } else {
      setCatalogue(catalogueResult.data || []);
      setRecords(recordsResult.data || []);
    }

    setLoading(false);
  }, [profile, session?.user?.id, suppliedProfile]);

  useEffect(() => {
    void load();
  }, [session?.user?.id]);

  const recordByCompetency = useMemo(
    () => Object.fromEntries(records.map((record) => [record.competency_id, record])),
    [records],
  );

  const required = useMemo(
    () => catalogue.filter((item) => isCompetencyRequiredForRole(item, profile?.job_role)),
    [catalogue, profile?.job_role],
  );

  const held = useMemo(
    () => records
      .map((record) => ({
        record,
        definition: catalogue.find((item) => item.id === record.competency_id) || null,
      }))
      .filter((item) => item.definition),
    [catalogue, records],
  );

  const requiredRows = useMemo(
    () => required.map((definition) => {
      const record = recordByCompetency[definition.id] || null;
      return {
        definition,
        record,
        status: getCompetencyStatus(record, { required: true }),
      };
    }),
    [recordByCompetency, required],
  );

  const validCount = held.filter(({ record }) => getCompetencyStatus(record).key === "valid").length;
  const expiringCount = held.filter(({ record }) => getCompetencyStatus(record).key === "expiring").length;
  const expiredCount = held.filter(({ record }) => getCompetencyStatus(record).key === "expired").length;
  const missingRequiredCount = requiredRows.filter(({ status }) => status.key === "missing").length;

  async function selfVerify(record) {
    if (!record?.id || verifyingRecordId) return;
    const notes = window.prompt(
      "Optional self-verification note (for example, where the evidence can be checked):",
      record.verification_notes || "",
    );
    if (notes === null) return;

    setVerifyingRecordId(record.id);
    setVerificationFeedback(null);
    const { error } = await supabase.rpc("self_verify_staff_competency", {
      p_record_id: record.id,
      p_notes: String(notes || "").trim() || null,
    });
    setVerifyingRecordId(null);

    if (error) {
      console.error("Unable to self-verify competency:", error);
      setVerificationFeedback({ type: "error", text: error.message || "Your competency could not be self-verified." });
      return;
    }

    setVerificationFeedback({ type: "success", text: "Competency self-verified successfully." });
    await load();
  }

  function verificationLabel(record) {
    if (!record?.verified) return "Awaiting verification";
    return record.verification_method === "management" ? "Management verified" : "Self verified";
  }

  if (loading) return <PageLoader label="Loading your competencies" />;

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />

      <main className="pguh-main">
        <div className="nhsuk-width-container">
          <section className="pguh-dashboard-hero pguh-competency-hero">
            <div>
              <span className="pguh-eyebrow">Staff development</span>
              <h1 className="nhsuk-heading-xl">Competencies & certifications</h1>
              <p className="nhsuk-body-l">
                Review your verified competencies, expiry dates and requirements for your current job role.
              </p>
            </div>
            <div className="pguh-dashboard-identity">
              <span>Current job role</span>
              <strong>{profile?.job_role || "Not assigned"}</strong>
              <small>Management controls role requirements</small>
            </div>
          </section>

          {feedback && (
            <div className="nhsuk-warning-callout" role="alert">
              <h2 className="nhsuk-warning-callout__label">Competency tracker unavailable</h2>
              <p>{feedback}</p>
              <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={load}>Try again</button>
            </div>
          )}

          {verificationFeedback && (
            <div className={`pguh-inline-notice pguh-inline-notice--${verificationFeedback.type}`} role="status">
              {verificationFeedback.text}
            </div>
          )}

          <section className="pguh-dashboard-stats" aria-label="Competency summary">
            <div className="pguh-stat-card">
              <span>Valid</span>
              <strong>{validCount}</strong>
              <small>Verified and in date</small>
            </div>
            <div className="pguh-stat-card">
              <span>Expiring within 30 days</span>
              <strong>{expiringCount}</strong>
              <small>Renewal should be planned</small>
            </div>
            <div className="pguh-stat-card">
              <span>Expired</span>
              <strong>{expiredCount}</strong>
              <small>Requires attention</small>
            </div>
            <div className="pguh-stat-card">
              <span>Missing role requirements</span>
              <strong>{missingRequiredCount}</strong>
              <small>{profile?.job_role || "No job role assigned"}</small>
            </div>
          </section>

          <section className="pguh-panel" aria-labelledby="required-competencies-heading">
            <div className="pguh-panel__header">
              <div>
                <span className="pguh-eyebrow">Role compliance</span>
                <h2 id="required-competencies-heading" className="nhsuk-heading-l">Required for my role</h2>
              </div>
            </div>

            {!profile?.job_role ? (
              <div className="pguh-empty-state">
                <FaIdBadge aria-hidden="true" />
                <h3>No job role has been assigned</h3>
                <p>Management can assign a job role to your staff profile before role-based competency requirements can be calculated.</p>
              </div>
            ) : requiredRows.length === 0 ? (
              <div className="pguh-empty-state">
                <FaCheckCircle aria-hidden="true" />
                <h3>No competency requirements configured</h3>
                <p>There are currently no mandatory competencies linked to {profile.job_role}.</p>
              </div>
            ) : (
              <div className="pguh-competency-grid">
                {requiredRows.map(({ definition, record, status }) => (
                  <article className="pguh-competency-card" key={definition.id}>
                    <div className="pguh-competency-card__topline">
                      <span className={competencyStatusClass(status)}>{status.label}</span>
                      <span className="pguh-competency-category">{definition.category || "General"}</span>
                    </div>
                    <h3>{definition.title}</h3>
                    <p>{definition.description || "No additional guidance has been provided."}</p>
                    <dl className="pguh-compact-details">
                      <div><dt>Achieved</dt><dd>{formatCompetencyDate(record?.achieved_on, "Not recorded")}</dd></div>
                      <div><dt>Expires</dt><dd>{formatCompetencyDate(record?.expires_on, record ? "No expiry" : "Not recorded")}</dd></div>
                      <div><dt>Certificate</dt><dd>{record?.certificate_reference || "Not recorded"}</dd></div>
                      <div><dt>Verification</dt><dd>{record ? verificationLabel(record) : "Missing"}</dd></div>
                    </dl>
                    {record?.evidence_url && (
                      <a className="pguh-text-link" href={record.evidence_url} target="_blank" rel="noreferrer">
                        View evidence <FaExternalLinkAlt aria-hidden="true" />
                      </a>
                    )}
                    {record && !record.verified && isEnabled("feature.competency_self_verification") && (
                      <button
                        className="nhsuk-button nhsuk-button--secondary pguh-competency-verify-button"
                        type="button"
                        disabled={verifyingRecordId === record.id}
                        onClick={() => selfVerify(record)}
                      >
                        <FaCheckCircle aria-hidden="true" /> {verifyingRecordId === record.id ? "Verifying…" : "Verify my competency"}
                      </button>
                    )}
                    {record?.verified && (
                      <small className="pguh-verification-meta">{verificationLabel(record)}{record.verified_at ? ` · ${new Date(record.verified_at).toLocaleDateString("en-GB")}` : ""}</small>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="pguh-panel" aria-labelledby="held-certifications-heading">
            <div className="pguh-panel__header">
              <div>
                <span className="pguh-eyebrow">My record</span>
                <h2 id="held-certifications-heading" className="nhsuk-heading-l">All recorded competencies</h2>
              </div>
              <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={load}>Refresh</button>
            </div>

            {held.length === 0 ? (
              <div className="pguh-empty-state">
                <FaAward aria-hidden="true" />
                <h3>No certifications recorded</h3>
                <p>Verified competency and certification records added by Management will appear here.</p>
              </div>
            ) : (
              <div className="pguh-table-scroll">
                <table className="nhsuk-table nhsuk-table--striped pguh-competency-table">
                  <thead>
                    <tr>
                      <th>Competency</th>
                      <th>Category</th>
                      <th>Achieved</th>
                      <th>Expires</th>
                      <th>Status</th>
                      <th>Verification</th>
                      <th>Reference</th>
                      <th>Evidence</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {held.map(({ record, definition }) => {
                      const requiredForRole = isCompetencyRequiredForRole(definition, profile?.job_role);
                      const status = getCompetencyStatus(record, { required: requiredForRole });
                      return (
                        <tr key={record.id}>
                          <td>
                            <strong>{definition.title}</strong>
                            {requiredForRole && <small className="pguh-table-subtext">Required for your role</small>}
                          </td>
                          <td>{definition.category || "General"}</td>
                          <td>{formatCompetencyDate(record.achieved_on)}</td>
                          <td>{formatCompetencyDate(record.expires_on, "No expiry")}</td>
                          <td><span className={competencyStatusClass(status)}>{status.label}</span></td>
                          <td>
                            <strong className="pguh-verification-state">{verificationLabel(record)}</strong>
                            {record.verified_at && <small className="pguh-table-subtext">{new Date(record.verified_at).toLocaleString("en-GB")}</small>}
                          </td>
                          <td>{record.certificate_reference || "—"}</td>
                          <td>
                            {record.evidence_url ? (
                              <a href={record.evidence_url} target="_blank" rel="noreferrer" aria-label={`Open evidence for ${definition.title}`}>
                                Open <FaExternalLinkAlt aria-hidden="true" />
                              </a>
                            ) : "—"}
                          </td>
                          <td>
                            {!record.verified && isEnabled("feature.competency_self_verification") ? (
                              <button
                                className="nhsuk-button nhsuk-button--secondary pguh-competency-verify-button pguh-competency-verify-button--table"
                                type="button"
                                disabled={verifyingRecordId === record.id}
                                onClick={() => selfVerify(record)}
                              >
                                {verifyingRecordId === record.id ? "Verifying…" : "Self verify"}
                              </button>
                            ) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {(expiringCount > 0 || expiredCount > 0 || missingRequiredCount > 0) && (
            <div className="nhsuk-warning-callout">
              <h2 className="nhsuk-warning-callout__label">
                <span role="text"><FaExclamationTriangle aria-hidden="true" /> Competency attention required</span>
              </h2>
              <p>
                Expiring, expired or missing role requirements should be discussed with your line manager. Renewal E-Learning may be assigned automatically where Management has linked a renewal module.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
